import React from 'react'
import { Typography, Box } from '@mui/material'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import {
  ValidationDialog,
  ValidationDialogTitleBox,
  ValidationRuleItem,
  ValidationRuleName,
  ValidationRuleDetail,
  ValidationRuleError,
  StyledDialogTitle,
  StyledDialogContent,
  StyledDialogActions,
  AlertContainer,
  DialogCancelButton,
} from './styled'

const WeightValidationDialog = ({
  open,
  onClose,
  onConfirm,
  missingWeightRules,
  invalidWeightRanges = [],
  invalidInvoiceRanges = [],
  missingFallbackCouriers = [],
  missingZoneRules = [],
}) => {
  return (
    <ValidationDialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <StyledDialogTitle>
        <ValidationDialogTitleBox>
          <WarningAmberIcon color='warning' />
          Missing Required Information
        </ValidationDialogTitleBox>
      </StyledDialogTitle>
      <StyledDialogContent>
        {missingWeightRules.length > 0 && (
          <>
            <Typography variant='body2' gutterBottom>
              The following rules are missing weight information (Min Weight or Max Weight):
            </Typography>
            <Box mb={2.5}>
              {missingWeightRules.map((rule) => (
                <ValidationRuleItem key={rule.id} severity='warning'>
                  <ValidationRuleName>{rule.name}</ValidationRuleName>
                  <ValidationRuleDetail>
                    Priority {rule.priority} &bull; {rule.deliveryType} / {rule.dispatchMode || 'Both'} /{' '}
                    {rule.deliveryType === 'RVP' ? rule.qcType || '' : rule.paymentMode || ''}
                  </ValidationRuleDetail>
                </ValidationRuleItem>
              ))}
            </Box>
          </>
        )}

        {invalidWeightRanges.length > 0 && (
          <>
            <Typography variant='body2' gutterBottom>
              The following rules have invalid weight ranges (Min Weight is greater than Max Weight):
            </Typography>
            <Box mb={2.5}>
              {invalidWeightRanges.map((rule) => (
                <ValidationRuleItem key={rule.id} severity='error'>
                  <ValidationRuleName>{rule.name}</ValidationRuleName>
                  <ValidationRuleDetail>
                    Priority {rule.priority} &bull; {rule.deliveryType} / {rule.dispatchMode || 'Both'} /{' '}
                    {rule.deliveryType === 'RVP' ? rule.qcType || '' : rule.paymentMode || ''}
                  </ValidationRuleDetail>
                  <ValidationRuleError>
                    Min Weight: {rule.advancedSettings.minWeight} kg &bull; Max Weight:{' '}
                    {rule.advancedSettings.maxWeight} kg
                  </ValidationRuleError>
                </ValidationRuleItem>
              ))}
            </Box>
          </>
        )}

        {invalidInvoiceRanges.length > 0 && (
          <>
            <Typography variant='body2' gutterBottom>
              The following rules have invalid invoice value ranges (Min Invoice Value is greater than
              Max Invoice Value):
            </Typography>
            <Box mb={2.5}>
              {invalidInvoiceRanges.map((rule) => (
                <ValidationRuleItem key={rule.id} severity='error'>
                  <ValidationRuleName>{rule.name}</ValidationRuleName>
                  <ValidationRuleDetail>
                    Priority {rule.priority} &bull; {rule.deliveryType} / {rule.dispatchMode || 'Both'} /{' '}
                    {rule.deliveryType === 'RVP' ? rule.qcType || '' : rule.paymentMode || ''}
                  </ValidationRuleDetail>
                  <ValidationRuleError>
                    Min Invoice Value: &#8377;{rule.advancedSettings.minInvoice} &bull; Max Invoice Value: &#8377;
                    {rule.advancedSettings.maxInvoice}
                  </ValidationRuleError>
                </ValidationRuleItem>
              ))}
            </Box>
          </>
        )}

        {missingFallbackCouriers.length > 0 && (
          <>
            <Typography variant='body2' gutterBottom>
              The following rules are missing fallback courier partners (at least 1 is required):
            </Typography>
            <Box mb={2.5}>
              {missingFallbackCouriers.map((rule) => (
                <ValidationRuleItem key={rule.id} severity='error'>
                  <ValidationRuleName>{rule.name}</ValidationRuleName>
                  <ValidationRuleDetail>
                    Priority {rule.priority} &bull; {rule.deliveryType} / {rule.dispatchMode || 'Both'} /{' '}
                    {rule.deliveryType === 'RVP' ? rule.qcType || '' : rule.paymentMode || ''}
                  </ValidationRuleDetail>
                </ValidationRuleItem>
              ))}
            </Box>
          </>
        )}

        {missingZoneRules.length > 0 && (
          <>
            <Typography variant='body2' gutterBottom>
              The following rules are missing a Zone (Zone is required):
            </Typography>
            <Box mb={2.5}>
              {missingZoneRules.map((rule) => (
                <ValidationRuleItem key={rule.id} severity='error'>
                  <ValidationRuleName>{rule.name}</ValidationRuleName>
                  <ValidationRuleDetail>
                    Priority {rule.priority} &bull; {rule.deliveryType} / {rule.dispatchMode || 'Both'} /{' '}
                    {rule.deliveryType === 'RVP' ? rule.qcType || '' : rule.paymentMode || ''}
                  </ValidationRuleDetail>
                  <ValidationRuleError>Zone is required — please select a zone in Advanced Settings.</ValidationRuleError>
                </ValidationRuleItem>
              ))}
            </Box>
          </>
        )}

        <AlertContainer severity='warning'>
          <Typography variant='body2'>
            <strong>Warning:</strong> Exporting rules without required information (weight or
            fallback couriers) or with invalid weight ranges (min weight greater than max weight) or
            invalid invoice value ranges (min invoice value greater than max invoice value) will
            cause errors during upload. Do you want to continue with the export?
          </Typography>
        </AlertContainer>
      </StyledDialogContent>
      <StyledDialogActions>
        <DialogCancelButton onClick={onClose} variant='contained' color='warning'>
          Cancel
        </DialogCancelButton>
      </StyledDialogActions>
    </ValidationDialog>
  )
}

export default WeightValidationDialog
